import { action, observable, computed } from "mobx";
import { User } from "oidc-client";
import { computedFn } from "mobx-utils";
import { LoadingStore } from "@/store/loadingStore";
import { getCouncilConfiguration, CouncilConfigurationModel, MapProvider } from "@/api/CouncilConfiguration";
import mapStore from "@/store/mapStore";
import requestStore from "@/store/requestStore";

export class UserStore {
  private resolveUserPromise!: (value?: User | PromiseLike<User | null> | null | undefined) => void;

  constructor() {
    this.userPromise = new Promise<User | null>(resolve => (this.resolveUserPromise = resolve));
    this.user = null;
    this.loading.track(() => this.userPromise, "loadUser");
  }

  @observable loading = new LoadingStore();

  @observable user: User | null;
  @observable userPromise: Promise<User | null>;
  @observable activeCouncil: string | null = null;
  @observable councilConfiguration: CouncilConfigurationModel | null = null;
  @observable isImageMaximized = false;

  @action.bound setUser(user: User | null) {
    if (user !== null) this.resolveUserPromise(user);
    this.user = user;

    if (user && !this.activeCouncil) {
      const activeCouncil = localStorage.getItem("activeCouncil");
      if (activeCouncil) this.setActiveCouncil(activeCouncil);
    }
  }

  @action.bound async setActiveCouncil(councilName: string) {
    this.loading.track(async () => {
      if (this.activeCouncil === councilName) return;
      localStorage.setItem("activeCouncil", councilName);
      this.activeCouncil = councilName;

      const response = await getCouncilConfiguration();
      this.councilConfiguration = response.configuration;

      mapStore.isPropertyBoundaryLayerVisible = this.councilConfiguration.isPropertyBoundaryEnabledByDefault;
    }, "setActiveCouncil");
  }

  @computed get availableCouncils() {
    if (this.user) {
      const councils = this.user.profile["dcp-council"];
      if (typeof councils === "string") return [councils];
      return [...this.user.profile["dcp-council"]].sort();
    }
    return [];
  }

  hasRole = computedFn((role: string) => {
    if (!this.user) return false;
    const roles: string | string[] | undefined = this.user.profile.roles;
    if (!roles) return false;
    if (typeof roles === "string") {
      return roles === role;
    }
    return roles.indexOf(role) !== -1;
  });

  canAccessVideoRequests() {
    if (!this.councilConfiguration) return false;
    if (this.isStaff()) {
      return this.councilConfiguration.isVideoRequestsEnabledForJJRUsers && (this.hasRole("Council Admin") || this.hasRole("Council Supervisor") || this.isVideoAllowed());
    } else {
      return this.councilConfiguration.isVideoRequestsEnabledForCouncilUsers && (this.hasRole("Council Admin") || this.hasRole("Council Supervisor") || this.isVideoAllowed());
    }
  }

  showSpectrumPropertyBoundaries() {
    if (!this.councilConfiguration) return false;
    return this.councilConfiguration.mapProvider == MapProvider.Old;
  }

  isStaff() {
    return this.user?.profile["jjs-staff"] === "true";
  }

  isVideoAllowed() {
    return this.user?.profile["allow-video"] === "true";
  }

  isMonthlyReportRead() {
    return this.user?.profile["monthly-report-read"] === "true";
  }

  isMonthlyReportAdmin() {
    return this.user?.profile["monthly-report-admin"] === "true";
  }
}

const userStore = new UserStore();

// eslint-disable-next-line @typescript-eslint/no-explicit-any
(window as any).userStore = userStore;

export default userStore;
